"use client";

import { createApi } from "@reduxjs/toolkit/query/react";
import qs from "qs";

import baseQuery from "@/libs/shared/api/baseQuery";
import {
  CreateEventBody,
  GetCalendarQuery,
  GetCalendarResponse,
  GetEventResponse,
  ListCalendarsQuery,
  ListCustomersResponse,
  ListGroupsQuery,
  ListGroupsResponse,
  UpdateEventBody,
} from "@/libs/calendar/redux/calendar.types";

const tagTypes = ["Calendar" as const, "Events" as const];

export const invalidateCalendar = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) => {
        api.dispatch(calendarApi.util.invalidateTags([tag]));
      });
    });
  };
};

export const calendarApi = createApi({
  reducerPath: "calendarApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getCalendar: builder.query<GetCalendarResponse, GetCalendarQuery>({
      query: ({ customerId, startDate, endDate, search }) => {
        const searchString = qs.stringify({
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          search: search || undefined,
        });
        return {
          url: customerId
            ? `/pt/customers/${customerId}/events?order_by=start_datetime&order_by_direction=asc&${searchString}`
            : `get_calendar_events?${searchString}`,
        };
      },
      providesTags: ["Events"],
    }),
    createEvent: builder.mutation<void, CreateEventBody>({
      query: (body: any) => {
        return {
          url: `store_calendar_event`,
          method: "POST",
          body,
        };
      },
      onQueryStarted: invalidateCalendar(["Events"]),
    }),
    updateEvent: builder.mutation<void, UpdateEventBody>({
      query: (body) => {
        return {
          url: `store_calendar_event`,
          method: "POST",
          body,
        };
      },
      onQueryStarted: invalidateCalendar(["Events"]),
    }),
    deleteEvent: builder.mutation<void, string>({
      query: (id) => {
        return {
          url: `delete_calendar_event/${id}`,
          method: "DELETE",
        };
      },
      onQueryStarted: invalidateCalendar(["Events"]),
    }),
    getCustomers: builder.query<ListCustomersResponse, ListCalendarsQuery>({
      query: ({ fields, name, page, per_page, customer_id }) => ({
        url: `pt/customers?fields=${fields}&per_page=${per_page}&page=${page}&name=${name}&customer_id=${customer_id?.join(",") || ""}`,
      }),
    }),
    getGroups: builder.query<ListGroupsResponse, ListGroupsQuery>({
      query: ({ name, page, per_page, id }) => ({
        url: `pt/groups?per_page=${per_page}&page=${page}&name=${name}&id=${id?.join(",") || ""}`,
      }),
    }),
    getEvent: builder.query<GetEventResponse, { id: string }>({
      query: ({ id }) => ({
        url: `get_calendar_event/${id}`,
      }),
    }),
  }),
});

export const {
  useCreateEventMutation,
  useDeleteEventMutation,
  useGetCalendarQuery,
  useGetCustomersQuery,
  useGetEventQuery,
  useGetGroupsQuery,
  useUpdateEventMutation,
} = calendarApi;
